import type { CallLog, CatalogProvider } from "../types";

export default function Activity({
  logs,
  catalog,
}: {
  logs: CallLog[];
  catalog: CatalogProvider[];
}) {
  const iconFor = (providerId: string) =>
    catalog.find((p) => p.id === providerId)?.icon ?? "•";

  return (
    <section className="section">
      <div className="section-head">
        <div>
          <h2>Activity</h2>
          <p className="section-sub">
            The last 25 tool calls made through your MCP server.
          </p>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="empty">
          <p>No tool calls yet.</p>
          <p className="empty-sub">
            Connect a service, switch it on, then ask your AI client to use it.
          </p>
        </div>
      ) : (
        <ul className="activity">
          {logs.map((log) => (
            <li key={log.id} className={`activity-row ${log.ok ? "" : "activity-failed"}`}>
              <div className="activity-main">
                <span className="activity-icon">{iconFor(log.providerId)}</span>
                {/* Logs written before multi-account have no slug; back then the provider
                    id was the prefix, so it reconstructs the name they were called by. */}
                <code className="activity-tool">
                  {log.slug || log.providerId}__{log.tool}
                </code>
                <span className="activity-meta">
                  {log.ok ? (
                    <span className="pill pill-ok pill-xs">ok</span>
                  ) : (
                    <span className="pill pill-error pill-xs">failed</span>
                  )}
                  <span className="activity-duration">{log.durationMs}ms</span>
                  <span className="activity-time">{relativeTime(log.ts)}</span>
                </span>
              </div>
              {/* The reason a call failed is the whole point of looking here -- show it
                  inline rather than hiding it in a tooltip. Older failed logs may carry
                  no message, so guard on it. */}
              {!log.ok && log.error && (
                <p className="activity-error">{log.error}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Firestore serverTimestamp is null until the write lands, hence the null case. */
function relativeTime(ts: CallLog["ts"]): string {
  if (!ts) return "just now";

  const seconds = Math.floor((Date.now() - ts.toMillis()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
